import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, formatApiError } from "@/context/AuthContext";
import { useBranding } from "@/context/BrandingContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Zap, LogIn, ShieldCheck, GitBranch, ListChecks } from "lucide-react";
import { toast } from "sonner";

export default function Login() {
  const { login } = useAuth();
  const { branding } = useBranding();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e?.preventDefault();
    setLoading(true);
    try {
      await login(email.trim(), password);
      toast.success("Berhasil masuk");
      const next = new URLSearchParams(window.location.search).get("next");
      navigate(next && next.startsWith("/f/") ? next : "/");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Gagal masuk");
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-slate-50">
      {/* Left brand panel */}
      <div className="relative hidden lg:flex flex-col justify-between overflow-hidden bg-slate-900 p-12 text-white">
        <div className="absolute inset-0 bg-gradient-to-br from-indigo-600 via-purple-600 to-pink-500 opacity-90" />
        <div className="absolute -top-24 -right-24 h-96 w-96 rounded-full bg-white/10 blur-3xl" />
        <div className="absolute -bottom-32 -left-16 h-96 w-96 rounded-full bg-amber-400/20 blur-3xl" />
        <div className="relative z-10 flex items-center gap-3">
          <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-white/20 backdrop-blur overflow-hidden">
            {branding.logo ? <img src={branding.logo} alt="logo" className="h-12 w-12 object-contain" /> : <Zap className="h-6 w-6" fill="white" />}
          </div>
          <div>
            <p className="text-xl font-extrabold tracking-tight leading-none">{branding.app_name}</p>
            <p className="text-sm text-white/70 mt-1">Struktur Jelas · Rencana Jelas · Eksekusi Jelas</p>
          </div>
        </div>
        <div className="relative z-10 space-y-6">
          <h1 className="text-4xl font-extrabold leading-tight tracking-tight">
            Kelola misi, tugas & <br />akuntabilitas berjenjang.
          </h1>
          <div className="space-y-3 text-white/90">
            <div className="flex items-center gap-3"><GitBranch className="h-5 w-5" /><span>Struktur organisasi berbasis posisi</span></div>
            <div className="flex items-center gap-3"><ListChecks className="h-5 w-5" /><span>Delegasi berjenjang & verifikasi Maker–Checker</span></div>
            <div className="flex items-center gap-3"><ShieldCheck className="h-5 w-5" /><span>Disiplin waktu & audit log penuh</span></div>
          </div>
        </div>
        <p className="relative z-10 text-xs text-white/60">© 2026 ZOA Group · 5 Cabang Pontianak + Head Office</p>
      </div>

      {/* Right form panel */}
      <div className="flex items-center justify-center p-6 sm:p-12">
        <div className="w-full max-w-md zoa-fade-up">
          <div className="lg:hidden flex items-center gap-2.5 mb-8">
            <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-gradient-to-br from-indigo-500 to-pink-500 overflow-hidden">
              {branding.logo ? <img src={branding.logo} alt="logo" className="h-11 w-11 object-contain" /> : <Zap className="h-5 w-5 text-white" fill="white" />}
            </div>
            <div><p className="font-extrabold">{branding.app_name}</p></div>
          </div>
          <h2 className="text-3xl font-extrabold tracking-tight text-slate-900">Masuk ke akun Anda</h2>
          <p className="text-slate-500 mt-1 mb-8">Gunakan email dan kata sandi ZOA Group Anda.</p>

          <form onSubmit={submit} className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="email">Email</Label>
              <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                placeholder="nama@zoagroup.com" required data-testid="login-email-input" className="h-11" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="password">Kata Sandi</Label>
              <Input id="password" type="password" value={password} onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••" required data-testid="login-password-input" className="h-11" />
            </div>
            <Button type="submit" disabled={loading} data-testid="login-submit-button"
              className="w-full h-11 text-base font-semibold bg-indigo-600 hover:bg-indigo-700">
              <LogIn className="h-4 w-4 mr-2" />{loading ? "Memproses..." : "Masuk"}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}
